import jsPDF from "jspdf";

const COMPANY_LOGO_URL = "https://cdn.acsdev.in/fynryx/acs.png";


const trimImagePadding = (dataUrl) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      let data;
      try {
        data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      } catch (e) {
        resolve({ dataUrl, width: canvas.width, height: canvas.height });
        return;
      }

      let hasAlpha = false;
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 250) { hasAlpha = true; break; }
      }
      
      const isBackground = (r, g, b, a) =>
        hasAlpha ? a < 20 : (r > 235 && g > 235 && b > 235);

      let minX = canvas.width, minY = canvas.height, maxX = -1, maxY = -1;
      for (let yy = 0; yy < canvas.height; yy++) {
        for (let xx = 0; xx < canvas.width; xx++) {
          const idx = (yy * canvas.width + xx) * 4;
          if (!isBackground(data[idx], data[idx + 1], data[idx + 2], data[idx + 3])) {
            if (xx < minX) minX = xx;
            if (xx > maxX) maxX = xx;
            if (yy < minY) minY = yy;
            if (yy > maxY) maxY = yy;
          }
        }
      }

      if (maxX < minX || maxY < minY) {
        resolve({ dataUrl, width: canvas.width, height: canvas.height });
        return;
      }

      const w = maxX - minX + 1;
      const h = maxY - minY + 1;
      if (w === canvas.width && h === canvas.height) {
       
        resolve({ dataUrl, width: canvas.width, height: canvas.height });
        return;
      }

      const out = document.createElement("canvas");
      out.width = w;
      out.height = h;
      out.getContext("2d").drawImage(canvas, minX, minY, w, h, 0, 0, w, h);
      console.info(`Logo had ${canvas.width}x${canvas.height} px of padding — trimmed to ${w}x${h}.`);
      resolve({ dataUrl: out.toDataURL("image/png"), width: w, height: h });
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });


const tryFetchLogo = async (url, timeoutMs) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  let res;
  try {
    res = await fetch(url, { mode: "cors", signal: controller.signal, cache: "no-store" });
  } catch (networkErr) {
   
    throw new Error(
      networkErr?.name === "AbortError"
        ? `Timed out after ${timeoutMs}ms`
        : `fetch() rejected (commonly CORS, DNS, or the CDN being unreachable): ${networkErr?.message || networkErr}`
    );
  } finally {
    clearTimeout(timeoutId);
  }

  if (!res.ok) {
    throw new Error(`CDN responded with HTTP ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

  const trimmed = await trimImagePadding(dataUrl);
  if (!trimmed || !trimmed.width || !trimmed.height) {
    throw new Error("Fetched a response but couldn't decode it as an image.");
  }
  return trimmed;
};

const urlToBase64 = async (url, { attempts = 3, timeoutMs = 8000, backoffMs = 600 } = {}) => {
  if (!url) return null;

  let lastErr = null;
  for (let i = 1; i <= attempts; i++) {
    try {
      const result = await tryFetchLogo(url, timeoutMs);
      if (i > 1) console.info(`Logo loaded from CDN on attempt ${i}/${attempts}.`);
      return result;
    } catch (err) {
      lastErr = err;
      console.warn(`Logo fetch attempt ${i}/${attempts} failed: ${err.message}`);
      if (i < attempts) {
        await new Promise((r) => setTimeout(r, backoffMs * i)); // linear backoff
      }
    }
  }

  console.error(
    `Logo failed to load from ${url} after ${attempts} attempts. Last error: ${lastErr?.message}. ` +
      "If this consistently mentions CORS/'Failed to fetch', the CDN is not sending a reliable " +
      "Access-Control-Allow-Origin header on this file — that has to be fixed server-side; " +
      "no client-side retry logic can work around it. If it consistently times out, the CDN itself " +
      "is slow or rate-limiting repeated requests."
  );
  return null;
};

/* ───────── COLOR HELPERS ───────── */
const BRAND_START = [37, 99, 235];
const BRAND_END   = [209, 36, 101];
const LIGHT_BG    = [248, 250, 252];
const ALT_ROW_BG  = [241, 245, 249];
const BORDER      = [226, 232, 240];
const TEXT_DARK   = [30, 41, 59];
const TEXT_MUTED  = [100, 116, 139];
const WHITE       = [255, 255, 255];
const TABLE_HEAD  = [230, 236, 255];

const lerp = (a, b, t) => Math.round(a + (b - a) * t);

const drawGradientRect = (doc, x, y, w, h, start, end, steps = 60) => {
  const stepW = w / steps;
  for (let i = 0; i < steps; i++) {
    const t = i / (steps - 1);
    doc.setFillColor(
      lerp(start[0], end[0], t),
      lerp(start[1], end[1], t),
      lerp(start[2], end[2], t)
    );
    doc.rect(x + i * stepW, y, stepW + 0.5, h, "F");
  }
};

/* ───────── NUMBER FORMAT HELPERS ───────── */
const formatCurrency = (val) =>
  (Number(val) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ONES = [
  "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
  "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
  "Seventeen", "Eighteen", "Nineteen",
];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

const numberToWordsIndian = (num) => {
  num = Math.round(Number(num) || 0);
  if (num === 0) return "Zero";
  const two   = (n) => n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? " " + ONES[n % 10] : ""}`;
  const three = (n) => n < 100 ? two(n) : `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? " " + two(n % 100) : ""}`;
  const parts = [];
  if (Math.floor(num / 10000000))              parts.push(`${three(Math.floor(num / 10000000))} Crore`);
  if (Math.floor((num % 10000000) / 100000))   parts.push(`${three(Math.floor((num % 10000000) / 100000))} Lakh`);
  if (Math.floor((num % 100000) / 1000))       parts.push(`${three(Math.floor((num % 100000) / 1000))} Thousand`);
  if (num % 1000)                              parts.push(three(num % 1000));
  return parts.join(" ");
};

/* ───────── CELL HELPER (single line, used where content is short/known) ───────── */
const cell = (doc, text, x, y, w, h, opts = {}) => {
  const {
    align = "left", bold = false, fontSize = 8.5, color = TEXT_DARK,
    bg = null, padX = 5, padY = null, border = true, borderColor = BORDER,
  } = opts;

  if (bg) {
    doc.setFillColor(...bg);
    doc.rect(x, y, w, h, "F");
  }
  if (border) {
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.4);
    doc.rect(x, y, w, h, "S");
  }

  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...color);

  const textY = y + (padY !== null ? padY : h / 2 + fontSize * 0.35);
  let textX = x + padX;
  if (align === "center") textX = x + w / 2;
  else if (align === "right") textX = x + w - padX;

  doc.text(String(text ?? ""), textX, textY, { align });
};


const wrapLines = (doc, text, w, fontSize, padX = 5) => {
  doc.setFontSize(fontSize);
  const maxW = Math.max(w - padX * 2, 10);
  return doc.splitTextToSize(String(text ?? ""), maxW);
};

const drawWrapCell = (doc, lines, x, y, w, h, opts = {}) => {
  const {
    align = "left", bold = false, fontSize = 7.2, color = TEXT_DARK,
    bg = null, padX = 5, border = true, borderColor = BORDER, lineHeight = 8.6,
  } = opts;

  if (bg) {
    doc.setFillColor(...bg);
    doc.rect(x, y, w, h, "F");
  }
  if (border) {
    doc.setDrawColor(...borderColor);
    doc.setLineWidth(0.4);
    doc.rect(x, y, w, h, "S");
  }

  doc.setFont("helvetica", bold ? "bold" : "normal");
  doc.setFontSize(fontSize);
  doc.setTextColor(...color);

  const totalTextH = lines.length * lineHeight;
  let textY = y + (h - totalTextH) / 2 + lineHeight * 0.78;
  let textX = x + padX;
  if (align === "center") textX = x + w / 2;
  else if (align === "right") textX = x + w - padX;

  lines.forEach((line) => {
    doc.text(line, textX, textY, { align });
    textY += lineHeight;
  });
};


const drawLogoPlaceholder = (doc, x, y, w, h, companyName) => {
  const initial = (companyName || "?").trim().charAt(0).toUpperCase() || "?";
  const cx = x + w / 2;
  const cy = y + h / 2;
  const r  = Math.min(w, h) / 2;

  doc.setFillColor(...BRAND_START);
  doc.circle(cx, cy, r, "F");
  drawGradientRect(doc, cx - r, cy - r, r * 2, r * 2, BRAND_START, BRAND_END, 40);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(Math.min(w, h) * 0.5);
  doc.setTextColor(...WHITE);
  doc.text(initial, cx, cy + Math.min(w, h) * 0.17, { align: "center" });
};

/* ═══════════════════════════════════════════════════════════════
   MAIN EXPORT
═══════════════════════════════════════════════════════════════ */
const generatePaySlipPDF = async (data = {}) => {
  const {
    companyName      = "ALGORITHMICA CONSULTANCY SERVICES PRIVATE LIMITED",
    companyAddress   = "",
    companyContact   = "",
    companyLogoBase64 = null,
    hideLogoPlaceholder = false,

    empId            = "",
    employeeName     = "",
    designation      = "",
    department       = "",
    location         = "",
    level            = "",
    dateOfBirth      = "",
    dateOfJoining    = "",
    bankName         = "",
    bankAccountNo    = "",
    panNo            = "",
    uanNo            = "",
    pfNo             = "",
    taxRegime        = "",
    month            = "",
    year             = "",
    monthYear        = "",
    totalWorkingDays = 0,
    lopDays          = 0,
    netPaidDays      = 0,
    earnings         = {},
    deductions       = {},
  } = data;

  /* ── Earnings rows ── */
  const earningRows = earnings.rows || [
    { label: "Basic Salary",              monthly: earnings.basicSalary             || 0 },
    { label: "HRA",                       monthly: earnings.hra                     || 0 },
    { label: "Bonus",                     monthly: earnings.bonus                   || 0 },
    { label: "Conveyance Allowances",     monthly: earnings.conveyanceAllowances    || 0 },
    { label: "Comp-off Encashment",       monthly: earnings.compOffEncashment       || 0 },
    { label: "Level 1 to 4 - PBL",       monthly: earnings.levelPBL                || 0 },
    { label: "Other Incentive",           monthly: earnings.otherIncentive          || 0 },
    { label: "Overtime Incentive",        monthly: earnings.overtimeIncentive       || 0 },
    { label: "Performance Incentive Arr", monthly: earnings.performanceIncentiveArr || 0 },
  ];

  /* ── Deduction rows ── */
  const deductionRows = deductions.rows || [
    { label: "ESI",                    monthly: deductions.esi                   || 0 },
    { label: "Transportation Charges", monthly: deductions.transportationCharges || 0 },
    { label: "PT",                     monthly: deductions.pt                    || 0 },
  ];

  const totalEarnings    = earningRows.reduce((s, r)   => s + (Number(r.monthly) || 0), 0);
  const totalDeductions  = deductionRows.reduce((s, r) => s + (Number(r.monthly) || 0), 0);
  const netPay           = totalEarnings - totalDeductions;
  const displayMonthYear = monthYear || `${month} - ${year}`;

  /* ── Resolve logo to base64 (preserving natural aspect ratio) ── */
  const resolvedLogoInput = companyLogoBase64 || COMPANY_LOGO_URL;
  let logoDataUrl  = null;
  let logoNaturalW = null;
  let logoNaturalH = null;

  if (resolvedLogoInput) {
    const loaded = await urlToBase64(resolvedLogoInput);
    if (loaded) {
      logoDataUrl  = loaded.dataUrl;
      logoNaturalW = loaded.width;
      logoNaturalH = loaded.height;
    }
  }

  /* ── PDF Setup ── */
  const doc      = new jsPDF("portrait", "pt", "a4");
  const pageW    = doc.internal.pageSize.getWidth();
  const margin   = 36;
  const contentW = pageW - margin * 2;
  let y          = margin;

 
  const logoMaxW    = 90;    
  const logoMinH    = 32;   
  const logoPad     = 0;     
  const logoOverlap = 16;    
  const logoOffsetX = 48;  

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  const nameLines = doc.splitTextToSize(companyName, contentW - logoMaxW - logoPad);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  const addrLines    = companyAddress ? doc.splitTextToSize(companyAddress, contentW - logoMaxW - logoPad) : [];
  const contactLines = companyContact ? [companyContact] : [];

  const nameLineH  = 14;
  const smallLineH = 10;
  const totalTextH = nameLines.length * nameLineH + addrLines.length * smallLineH + contactLines.length * smallLineH;

  // Logo height matches the text block height, but never shrinks below logoMinH.
  const logoTargetH = Math.max(totalTextH, logoMinH);

  // Scale logo to logoTargetH, capped by logoMaxW on width.
  let logoDrawW = 0;
  let logoDrawH = 0;
  if (logoDataUrl && logoNaturalW && logoNaturalH) {
    const scaleByHeight = logoTargetH / logoNaturalH;
    const wIfHeightMatched = logoNaturalW * scaleByHeight;
    if (wIfHeightMatched <= logoMaxW) {
      logoDrawH = logoTargetH;
      logoDrawW = wIfHeightMatched;
    } else {
      const scaleByWidth = logoMaxW / logoNaturalW;
      logoDrawW = logoMaxW;
      logoDrawH = logoNaturalH * scaleByWidth;
    }
  } else if (!hideLogoPlaceholder) {
    // Placeholder badge is drawn as a square at logoTargetH.
    logoDrawW = logoTargetH;
    logoDrawH = logoTargetH;
  }

  // Text starts right after the logo's ACTUAL width — no leftover gap.
  const textAreaX    = margin + logoOffsetX + logoDrawW + (logoDrawW ? logoPad : 0) - (logoDrawW ? logoOverlap : 0);
  const textAreaW    = contentW - logoOffsetX - logoDrawW - (logoDrawW ? logoPad : 0) + (logoDrawW ? logoOverlap : 0);
  const headerStartY = y;
  const headerH       = Math.max(logoDrawH, totalTextH) + 16;
  const headerCenterY = headerStartY + headerH / 2;

  // Draw logo (or placeholder) — vertically centered on the same line as the text block
  if (logoDataUrl && logoDrawW && logoDrawH) {
    try {
      const fmt   = logoDataUrl.includes("image/png") ? "PNG" : "JPEG";
      const logoY = headerCenterY - logoDrawH / 2;
      doc.addImage(logoDataUrl, fmt, margin + logoOffsetX, logoY, logoDrawW, logoDrawH);
    } catch (e) {
      console.warn("Logo render failed, falling back to placeholder:", e);
      if (!hideLogoPlaceholder) {
        drawLogoPlaceholder(doc, margin + logoOffsetX, headerCenterY - logoDrawH / 2, logoDrawW, logoDrawH, companyName);
      }
    }
  } else if (!hideLogoPlaceholder && logoDrawW) {
    drawLogoPlaceholder(doc, margin + logoOffsetX, headerCenterY - logoDrawH / 2, logoDrawW, logoDrawH, companyName);
  }

  // Draw company text — block is centered on the same center line as the logo
  const textCenterX  = textAreaX + textAreaW / 2;
  const textBlockTop = headerCenterY - totalTextH / 2;
  let ty = textBlockTop + nameLineH * 0.78; // baseline offset for first line's ascent

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...TEXT_DARK);
  nameLines.forEach((l) => { doc.text(l, textCenterX, ty, { align: "center" }); ty += nameLineH; });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...TEXT_MUTED);
  addrLines.forEach((l)    => { doc.text(l, textCenterX, ty, { align: "center" }); ty += smallLineH; });
  contactLines.forEach((l) => { doc.text(l, textCenterX, ty, { align: "center" }); ty += smallLineH; });

  y = headerStartY + headerH + 4;

  // Gradient divider
  drawGradientRect(doc, margin, y, contentW, 2, BRAND_START, BRAND_END);
  y += 12;

  // Payslip title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9.5);
  doc.setTextColor(...TEXT_DARK);
  doc.text(`Payslip For The Month of : ${displayMonthYear}`, pageW / 2, y, { align: "center" });
  y += 16;

  /* ══ EMPLOYEE DETAILS TABLE (responsive: wraps + grows row height) ══ */
  const empRows = [
    ["Employee ID",        empId || "-",                    "Employee Name",   employeeName || "-",  "Bank Name",       bankName || "-"],
    ["Level",              level || "-",                    "Designation",     designation || "-",   "Bank Account No", bankAccountNo || "-"],
    ["Date Of Joining",    dateOfJoining || "-",            "Department",      department || "-",    "Month & Year",    displayMonthYear],
    ["Date Of Birth",      dateOfBirth || "-",              "Location",        location || "-",      "PAN",             panNo || "-"],
    ["UAN",                uanNo || "-",                    "PF No.",          pfNo || "-",          "Tax Regime",      taxRegime || "-"],
    ["Total Working Days", String(totalWorkingDays || "-"), "LOP Days",        String(lopDays || "0"), "Net Paid Days", String(netPaidDays || "-")],
  ];

  const labelW      = Math.round(contentW * 0.149);
  const valueW      = Math.round(contentW * 0.184);
  const pairW       = labelW + valueW;
  const baseRowH    = 20;
  const empFontSize = 7.2;
  const lineHeight  = 8.6;

  empRows.forEach((row) => {
    const cellsInfo = [];
    let maxLines = 1;
    for (let col = 0; col < 3; col++) {
      const x  = margin + col * pairW;
      const vw = col === 2 ? (margin + contentW) - (x + labelW) : valueW;

      const labelLines = wrapLines(doc, row[col * 2],     labelW, empFontSize);
      const valueLines = wrapLines(doc, row[col * 2 + 1], vw,     empFontSize);

      maxLines = Math.max(maxLines, labelLines.length, valueLines.length);
      cellsInfo.push({ x, vw, labelLines, valueLines });
    }

    const rowH = Math.max(baseRowH, maxLines * lineHeight + 8);

    cellsInfo.forEach(({ x, vw, labelLines, valueLines }) => {
      drawWrapCell(doc, labelLines, x,          y, labelW, rowH, { fontSize: empFontSize, lineHeight, bg: LIGHT_BG, borderColor: BORDER, padX: 4 });
      drawWrapCell(doc, valueLines, x + labelW, y, vw,     rowH, { fontSize: empFontSize, lineHeight, bg: WHITE,    borderColor: BORDER, padX: 4 });
    });

    y += rowH;
  });

  y += 14;

  /* ══ EARNINGS & DEDUCTIONS TABLES ══ */
  const tableGap = 8;
  const tblW     = (contentW - tableGap) / 2;
  const hdrH     = 20;
  const tblRowH  = 18;
  const earnX    = margin;
  const dedX     = margin + tblW + tableGap;
  const eL       = Math.round(tblW * 0.68);
  const eA       = tblW - eL;

  cell(doc, "Earnings",   earnX,       y, eL, hdrH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, borderColor: BORDER, padX: 5 });
  cell(doc, "Monthly",    earnX + eL,  y, eA, hdrH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, borderColor: BORDER, align: "center" });
  cell(doc, "Deductions", dedX,        y, eL, hdrH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, borderColor: BORDER, padX: 5 });
  cell(doc, "Monthly",    dedX + eL,   y, eA, hdrH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, borderColor: BORDER, align: "center" });
  y += hdrH;

  const maxRows = Math.max(earningRows.length, deductionRows.length);
  for (let i = 0; i < maxRows; i++) {
    const bg = i % 2 === 1 ? ALT_ROW_BG : WHITE;
    const er = earningRows[i];
    const dr = deductionRows[i];
    cell(doc, er ? er.label : "",                   earnX,      y, eL, tblRowH, { fontSize: 8, bg, borderColor: BORDER, padX: 5 });
    cell(doc, er ? formatCurrency(er.monthly) : "", earnX + eL, y, eA, tblRowH, { fontSize: 8, bg, borderColor: BORDER, align: "right", padX: 5 });
    cell(doc, dr ? dr.label : "",                   dedX,       y, eL, tblRowH, { fontSize: 8, bg, borderColor: BORDER, padX: 5 });
    cell(doc, dr ? formatCurrency(dr.monthly) : "", dedX + eL,  y, eA, tblRowH, { fontSize: 8, bg, borderColor: BORDER, align: "right", padX: 5 });
    y += tblRowH;
  }

  cell(doc, "Total Earnings",                earnX,      y, eL, tblRowH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, borderColor: BORDER, padX: 5 });
  cell(doc, formatCurrency(totalEarnings),   earnX + eL, y, eA, tblRowH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, borderColor: BORDER, align: "right", padX: 5 });
  cell(doc, "Total Deduction",               dedX,       y, eL, tblRowH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, borderColor: BORDER, padX: 5 });
  cell(doc, formatCurrency(totalDeductions), dedX + eL,  y, eA, tblRowH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, borderColor: BORDER, align: "right", padX: 5 });
  y += tblRowH + 10;

  /* ══ SUMMARY BAR ══ */
  const sumH  = 20;
  const col3W = contentW / 3;

  cell(doc, "Earnings",   margin,           y, col3W, sumH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, align: "center", borderColor: BORDER });
  cell(doc, "Deductions", margin + col3W,   y, col3W, sumH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, align: "center", borderColor: BORDER });
  cell(doc, "Net Pay",    margin + col3W*2, y, col3W, sumH, { bold: true, fontSize: 8.5, bg: TABLE_HEAD, align: "center", borderColor: BORDER });
  y += sumH;

  cell(doc, formatCurrency(totalEarnings),   margin,           y, col3W, sumH, { fontSize: 8.5, bg: LIGHT_BG, align: "center", borderColor: BORDER });
  cell(doc, formatCurrency(totalDeductions), margin + col3W,   y, col3W, sumH, { fontSize: 8.5, bg: LIGHT_BG, align: "center", borderColor: BORDER });
  cell(doc, formatCurrency(netPay),          margin + col3W*2, y, col3W, sumH, { bold: true, fontSize: 8.5, bg: LIGHT_BG, align: "center", borderColor: BORDER });
  y += sumH;

  const wordsText = `RUPEES ${numberToWordsIndian(netPay).toUpperCase()} ONLY`;
  const wLblW     = contentW * 0.22;
  cell(doc, "Net Pay (in Words)", margin,          y, wLblW,             20, { bold: true, fontSize: 8, bg: LIGHT_BG, borderColor: BORDER });
  cell(doc, wordsText,            margin + wLblW,  y, contentW - wLblW, 20, { fontSize: 8,             bg: WHITE,    borderColor: BORDER });
  y += 36;

  /* ══ FOOTER ══ */
  drawGradientRect(doc, margin, y, contentW, 2, BRAND_START, BRAND_END);
  y += 10;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7.5);
  doc.setTextColor(...TEXT_MUTED);
  doc.text(
    "This is a computer generated payslip and does not require a signature.",
    margin, y
  );
  doc.text(
    `Generated on ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`,
    pageW - margin, y, { align: "right" }
  );

  /* ── Save ── */
  const safeName  = (employeeName || "Employee").replace(/\s+/g, "_");
  const safeMonth = displayMonthYear.replace(/\s/g, "_").replace(/-/g, "");
  doc.save(`PaySlip_${safeName}_${safeMonth}.pdf`);
};

export default generatePaySlipPDF;